import { Button, Col, Collapse, Divider, Image, Row, Spin, Table } from 'antd';
import PropTypes from 'prop-types';
import { useEffect, Fragment, useMemo, useCallback } from 'react';
import withStyles from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { EditOutlined, LoadingOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router';

import { clearSamplePreview, getSamplePreview } from '../Actions';
import Breadcrumbs from '../Components/Breadcrumbs';
import LeftGrid from '../Components/LeftGrid';
import RightGrid from '../Components/RightGrid';
import styles from '../styles';
import { apiURL } from '../config';
import constants from '../constants';
import { get } from '../utils';

const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

const PreviewSample = props => {
  const { classes, match } = props;

  const dispatch = useDispatch();
  const history = useHistory();

  const sample = useSelector(state => state.samplePreview);

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);

  useEffect(() => {
    dispatch(getSamplePreview(match.params.id));
    return () => {
      dispatch(clearSamplePreview());
    };
  }, []);

  const rowKey = useCallback(
    record => record.id || record.fabricId || record.accessoryId,
    []
  );

  const fabricColumns = useMemo(() => {
    const _columns = [
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'Consumption',
        dataIndex: 'consumption',
      },
      {
        title: 'Net Qty',
        dataIndex: 'quantity',
      },
      {
        title: 'Add %',
        dataIndex: 'additionalPercentage',
      },
      {
        title: 'Total Qty',
        dataIndex: 'totalQuantity',
      },
      {
        title: 'Stock',
        dataIndex: 'stock',
      },
      {
        title: 'To Order',
        dataIndex: 'toOrder',
      },
    ];

    return _columns;
  }, []);

  const accessoryColumns = useMemo(() => {
    const _columns = [
      {
        title: 'Accessory',
        dataIndex: 'accessory',
      },
      {
        title: 'Consumption',
        dataIndex: 'consumption',
      },
      {
        title: 'Net Qty',
        dataIndex: 'quantity',
      },
      {
        title: 'Add %',
        dataIndex: 'additionalPercentage',
      },
      {
        title: 'Total Qty',
        dataIndex: 'totalQuantity',
      },
      {
        title: 'Stock',
        dataIndex: 'stock',
      },
      {
        title: 'To Order',
        dataIndex: 'toOrder',
      },
    ];

    return _columns;
  }, []);

  const fabricsSummaryColumns = useMemo(() => {
    const _columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'Total Qty',
        dataIndex: 'totalQuantity',
      },
      {
        title: 'Stock',
        dataIndex: 'stock',
      },
      {
        title: 'To Order',
        dataIndex: 'toOrder',
      },
    ];

    return _columns;
  }, []);

  const accessoriesSummaryColumns = useMemo(() => {
    const _columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
      },
      {
        title: 'Total Qty',
        dataIndex: 'totalQuantity',
      },
      {
        title: 'Stock',
        dataIndex: 'stock',
      },
      {
        title: 'To Order',
        dataIndex: 'toOrder',
      },
    ];

    return _columns;
  }, []);

  const renderExistingImage = (image, index) => {
    return (
      <Fragment key={index}>
        <Col xs={24} sm={7} md={5} lg={3} xl={2}>
          <Image
            src={`${apiURL}/${image.key}`}
            className={classes.sampleImage}
          />
        </Col>
        <Col xs={0} sm={1} />
      </Fragment>
    );
  };

  const renderProduct = (product, productIndex, totalProducts) => {
    return (
      <Fragment key={productIndex}>
        <Row>
          <Col xs={24}>
            <span className={classes.previewSpan}>Product: </span>
            {product.product}
          </Col>
        </Row>
        {product.files && product.files.length ? (
          <>
            <br />
            <Row>
              <Image.PreviewGroup>
                {product.files.map(renderExistingImage)}
              </Image.PreviewGroup>
            </Row>
          </>
        ) : null}
        {product.styles.map((style, styleIndex) => {
          return (
            <Fragment key={styleIndex}>
              <Divider orientation="left" plain>
                Style {styleIndex + 1} &nbsp;&nbsp;
              </Divider>
              <Row>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>Style: </span>
                  {style.name}
                </Col>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>Description: </span>
                  {style.description}
                </Col>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>Size: </span>
                  {style.size}
                </Col>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>Base Color: </span>
                  {style.baseColor}
                </Col>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>Print Color: </span>
                  {style.printColor}
                </Col>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>
                    Embroidery Color:{' '}
                  </span>
                  {style.embroideryColor}
                </Col>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>Acid Wash Color: </span>
                  {style.acidWashColor}
                </Col>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>Quantity: </span>
                  {style.quantity}
                </Col>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>Additional: </span>
                  {style.additional}
                </Col>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>Total: </span>
                  {style.totalQuantity}
                </Col>
              </Row>
              <br />
              <Row>
                <Table
                  bordered
                  size="small"
                  columns={fabricColumns}
                  dataSource={style.fabrics}
                  rowKey={rowKey}
                  pagination={false}
                />
              </Row>
              {style.accessories && style.accessories.length ? (
                <>
                  <br />
                  <Row>
                    <Table
                      bordered
                      size="small"
                      columns={accessoryColumns}
                      dataSource={style.accessories}
                      rowKey={rowKey}
                      pagination={false}
                    />
                  </Row>
                </>
              ) : null}
            </Fragment>
          );
        })}
        {totalProducts - 1 > productIndex ? <Divider /> : null}
      </Fragment>
    );
  };

  return (
    <>
      <Breadcrumbs
        moduleName="Sample"
        moduleLink="/samples/list"
        subModuleName="Preview"
      />
      {sample.id ? (
        <>
          <Row>
            <LeftGrid>
              <span className={classes.previewSpan}>Customer: </span>
              {sample.customer}
            </LeftGrid>
            <RightGrid>
              <span className={classes.previewSpan}>Remark: </span>
              {sample.remark}
            </RightGrid>
          </Row>
          <br />
          <Row>
            {permissions.includes(constants.permissions.updateSample) ? (
              <LeftGrid>
                <Button
                  onClick={() =>
                    history.push(`/samples/edit/${match.params.id}`)
                  }
                  icon={<EditOutlined />}
                >
                  Update
                </Button>
              </LeftGrid>
            ) : null}
            {permissions.includes(constants.permissions.issueSampleItems) ? (
              <LeftGrid>
                <Button
                  onClick={() =>
                    history.push(`/samples/issue/${match.params.id}`)
                  }
                  icon={<EditOutlined />}
                >
                  Issue
                </Button>
              </LeftGrid>
            ) : null}
          </Row>
          <br />
          <Row>
            <Col xs={{ span: 22, offset: 1 }}>
              <Collapse defaultActiveKey={1}>
                <Collapse.Panel
                  key={1}
                  header="Products"
                  extra={`${
                    sample.updatedBy
                      ? `Updated By ${sample.updatedBy}`
                      : `Created By ${sample.createdBy}`
                  } at ${moment(sample.updatedAt || sample.createdAt).format(
                    'DD MMM YYYY'
                  )}`}
                >
                  {sample.products.map((product, index) =>
                    renderProduct(product, index, sample.products.length)
                  )}
                </Collapse.Panel>
              </Collapse>
              <br />
            </Col>
          </Row>
          <Row>
            <Col xs={{ span: 22, offset: 1 }}>
              {sample.fabricsSummary && sample.fabricsSummary.length ? (
                <>
                  <span className={classes.previewSpan}>Fabrics Summary:</span>
                  <br />
                  <br />
                  <Table
                    bordered
                    size="small"
                    columns={fabricsSummaryColumns}
                    dataSource={sample.fabricsSummary}
                    rowKey={rowKey}
                    pagination={false}
                  />
                </>
              ) : null}
              {sample.accessoriesSummary && sample.accessoriesSummary.length ? (
                <>
                  <span className={classes.previewSpan}>
                    Accessories Summary:
                  </span>
                  <br />
                  <br />
                  <Table
                    bordered
                    size="small"
                    columns={accessoriesSummaryColumns}
                    dataSource={sample.accessoriesSummary}
                    rowKey={rowKey}
                    pagination={false}
                  />
                </>
              ) : null}
            </Col>
          </Row>
        </>
      ) : (
        <Row className={classes.flexCenter}>
          <Spin indicator={antIcon} />
        </Row>
      )}
      <br />
    </>
  );
};

PreviewSample.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

export default withStyles(styles)(PreviewSample);
